import { Injectable } from '@angular/core';

import { switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { MessageType, ProblemDetails } from '../../../core/models';
import * as fromRoot from '../../../core/store';

import * as fromActions from '../actions';
import * as fromModels from '../../models';
import * as fromServices from '../../services';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

@Injectable()
export class CreditReportEffects {
  constructor(private actions$: Actions, private creditReportService: fromServices.CreditReportService) {}

  processQueryString$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.ProcessQueryString),
      switchMap((action: fromActions.ProcessQueryString) => {
        // Grab an object representation of the query string
        const query = this.parseQueryStringFromUrl(action.payload);
        // Convert query object into our strict-typed object
        let searchCriteria: fromModels.LoadSummaryRequest;

        // Populate search criteria with the query object params
        searchCriteria = {
          creditReportSourceType: query['creditReportSourceType'] ? query['creditReportSourceType'] : '',
          locationId: query['locationId'] ? query['locationId'] : '',
          fromDate: query['fromDate'] ? query['fromDate'] : '',
          toDate: query['toDate'] ? query['toDate'] : '',
          lastName: query['lastName'] ? query['lastName'] : '',
          socialSecurityNumber: query['socialSecurityNumber'] ? query['socialSecurityNumber'] : '',
          sort: query['sort'] ? query['sort'] : 'CreatedDateTime:Desc',
          skip: query['skip'] ? query['skip'] : 0,
          take: query['take'] ? query['take'] : 15
        };

        return of(new fromActions.LoadSummary(searchCriteria));
      })
    )
  );

  private parseQueryStringFromUrl(url: string): object {
    const returnObject: object = {};
    // Parse query string into chunks
    const splitted = url.split('?', 2);
    // If we have a query string, then process it further
    if (splitted.length == 2) {
      // Split out each key/value pair
      const splittedItems = splitted[1].split('&');
      splittedItems.forEach(item => {
        // Split out the key and value
        const splittedItem = item.split('=');
        const key = splittedItem[0];
        const value = splittedItem.length > 1 ? splittedItem[1] : '';
        // Add is as a key/value pair in our return object.
        returnObject[key] = value;
      });
    }
    return returnObject;
  }

  nextPageResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.NextPage),
      switchMap((action: fromActions.NextPage) => {
        const currentPage: number = action.pagingSorting.currentPage;
        let nextPage: number;

        // If page number is null, maybe we never got first page?
        if (currentPage == null) {
          // Go to the first one
          nextPage = 1;
        } else if (currentPage < action.pagingSorting.totalPages) {
          // Increment to next page
          nextPage = currentPage + 1;
        } else {
          // Don't change the page
          nextPage = currentPage;
        }

        const skip = (nextPage - 1) * action.pagingSorting.pageSize;
        const take = action.pagingSorting.pageSize;

        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadSummaryRequest = {
          ...action.payload,
          skip,
          take
        };
        // TODO: Figure out some way to not do any action here if page number didn't change
        return of(new fromActions.LoadSummary(newPayload));
      })
    )
  );

  prevPageResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.PrevPage),
      switchMap((action: fromActions.PrevPage) => {
        const currentPage: number = action.pagingSorting.currentPage;
        let prevPage: number;

        // If page number is null, maybe we never got first page?
        if (currentPage == null) {
          // Go to the first one
          prevPage = 1;
        } else if (currentPage > 1) {
          // Increment to next page
          prevPage = currentPage - 1;
        } else {
          // Don't change the page
          prevPage = currentPage;
        }

        const skip = (prevPage - 1) * action.pagingSorting.pageSize;
        const take = action.pagingSorting.pageSize;

        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadSummaryRequest = {
          ...action.payload,
          skip,
          take
        };
        // TODO: Figure out some way to not do any action here if page number didn't change
        return of(new fromActions.LoadSummary(newPayload));
      })
    )
  );

  sortByResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.SortBy),
      switchMap((action: fromActions.SortBy) => {
        // Default to ascending sort order
        let sortDir: 'Asc' | 'Desc' = 'Desc';

        console.log('action.pagingSorting[sort]', `${action.pagingSorting.sortBy}:${action.pagingSorting.sortDir}`);
        console.log('action.payload', action.payload);
        // If user is sorting by same column again
        if (action.payload == action.pagingSorting.sortBy) {
          // Change the direction of the sort since it's the same column
          sortDir = action.pagingSorting.sortDir == 'Asc' ? 'Desc' : 'Asc';
        }
        const lastPage: number = action.pagingSorting.totalPages;

        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadSummaryRequest = {
          ...action.searchCriteria,
          sort: action.payload + ':' + sortDir,
          skip: 0,
          take: action.pagingSorting.pageSize
        };

        return of(new fromActions.LoadSummary(newPayload));
      })
    )
  );

  lastPageResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.LastPage),
      switchMap((action: fromActions.LastPage) => {
        const lastPage: number = action.pagingSorting.totalPages;

        const skip = (lastPage - 1) * action.pagingSorting.pageSize;
        const take = action.pagingSorting.pageSize;
        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadSummaryRequest = {
          ...action.payload,
          skip,
          take
        };

        return of(new fromActions.LoadSummary(newPayload));
      })
    )
  );

  firstPageResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.FirstPage),
      switchMap((action: fromActions.FirstPage) => {
        const firstPage = 1;

        const skip = (firstPage - 1) * action.pagingSorting.pageSize;
        const take = action.pagingSorting.pageSize;

        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadSummaryRequest = {
          ...action.payload,
          skip,
          take
        };

        return of(new fromActions.LoadSummary(newPayload));
      })
    )
  );

  pageSizeResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.SetPageSize),
      switchMap((action: fromActions.SetPageSize) => {
        const pageSize: number = action.payload;

        // Set the next page to pass to our search request
        const newPayload: fromModels.LoadSummaryRequest = {
          ...action.searchCriteria,
          take: pageSize,
          skip: 0
        };

        return of(new fromActions.LoadSummary(newPayload));
      })
    )
  );

  searchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.ExecuteSearch),
      switchMap((action: fromActions.ExecuteCreditReportSearch) => {
        return of(
          new fromRoot.Go({
            path: ['credit-report-summary'],
            query: action.payload
          })
        );
      })
    )
  );

  loadCreditReportSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.LoadSummary),
      switchMap((action: fromActions.LoadSummary) => {
        // if (!action.payload.rentalCategoryCode && !action.payload.state) return of(new fromActions.LoadShippingChargesSuccess([]));

        return this.creditReportService.getCreditReportSummary(action.payload).pipe(
          map((response: HttpResponse<fromModels.LoadSummaryResponse>) => {
            if (!response.ok || !response.body) return new fromActions.LoadSummaryFailure('Search result not returned.');
            return new fromActions.LoadSummarySuccess(response.body, response.headers);
          }),
          catchError((error: HttpErrorResponse) => of(new fromActions.LoadSummaryFailure(error)))
        );
      })
    )
  );

  loadCreditReportSummaryFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.LoadSummaryFailure),
      switchMap((action: fromActions.LoadSummaryFailure) =>
        of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        )
      )
    )
  );

  downloadRequestForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.DownloadRequestForm),
      switchMap((action: fromActions.DownloadRequestForm) => {
        return this.creditReportService.downloadRequestForm(action.payload.id).pipe(
          map(success => {
            return new fromActions.DownloadRequestFormSuccess({ id: action.payload.id });
          }),
          catchError(error => {
            return of(new fromActions.DownloadRequestFormFailure({ id: action.payload.id, error: error }));
          })
        );
      })
    )
  );

  downloadRequestFormFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.DownloadRequestFormFailure),
      switchMap((action: fromActions.DownloadRequestFormFailure) => {
        return of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload.error),
            messageType: MessageType.alert,
            toast: false
          })
        );
      })
    )
  );

  downloadCreditCheckReportFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.DownloadSummarySearch),
      switchMap((action: fromActions.DownloadSummarySearch) => {
        return this.creditReportService.downloadCreditCheckReportFile(action.payload).pipe(
          map(success => {
            return new fromActions.DownloadSummarySearchSuccess(action.payload);
          }),
          catchError(error => {
            return of(new fromActions.DownloadSummarySearchFailure(error));
          })
        );
      })
    )
  );

  downloadCreditCheckReportFileFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.DownloadSummarySearchFailure),
      switchMap((action: fromActions.DownloadSummarySearchFailure) => {
        return of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        );
      })
    )
  );

  downloadReasonsReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.DownloadReasonsReport),
      switchMap((action: fromActions.DownloadReasonsReport) => {
        return this.creditReportService.downloadReasonsReport(action.payload).pipe(
          map(success => {
            return new fromActions.DownloadReasonsReportSuccess({ endDate: action.payload.endDate, startDate: action.payload.startDate });
          }),
          catchError(error => {
            return of(new fromActions.DownloadReasonsReportFailure(error));
          })
        );
      })
    )
  );

  downloadReasonsReportFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CreditReportActionTypes.DownloadReasonsReportFailure),
      switchMap((action: fromActions.DownloadReasonsReportFailure) => {
        return of(
          new fromRoot.DisplayMessage({
            message: this.getErrorMessage(action.payload),
            messageType: MessageType.alert,
            toast: false
          })
        );
      })
    )
  );

  private getErrorMessage(response: HttpErrorResponse | string): string {
    if (typeof response === 'string') return response;

    if (response.status === 0) {
      return 'Network error. Please check your internet connection.';
    }

    if (response.status === 400 && response.error) {
      try {
        const problemDetails = response.error as ProblemDetails;
        if (problemDetails) {
          const firstError = problemDetails.errors ? problemDetails.errors[Object.keys(problemDetails.errors)[0]] : null;
          if (firstError) return firstError;
          else if (problemDetails.title) return problemDetails.title;
        }
      } catch (ex) {
        console.warn(ex);
      }
    }

    return response.error ? response.error.message : response.message;
  }
}
